import { useState, useEffect, useCallback } from "react";
import { exercisesAPI } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Dumbbell, Pencil, Trash2, GitBranch } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "strength", label: "Fuerza" },
  { value: "mobility", label: "Movilidad" },
  { value: "stretch", label: "Estiramiento" },
  { value: "cardio", label: "Cardio" },
  { value: "balance", label: "Equilibrio" },
  { value: "other", label: "Otro" },
];
const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

const REL_TYPES = [
  { value: "progression", label: "Progresión (más difícil)" },
  { value: "regression", label: "Regresión (más fácil)" },
  { value: "variant", label: "Variante" },
  { value: "alternative", label: "Alternativa" },
];
const relTypeLabel = (t) => REL_TYPES.find((r) => r.value === t)?.label || t;

const EMPTY = { name: "", category: "strength", body_parts: "", equipment: "", tags: "", description: "", instructions: "" };

// "a, b ,c" -> ["a","b","c"]
const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function ExercisesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [scope, setScope] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // exercise id en edición o null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // progressions
  const [relExercise, setRelExercise] = useState(null);
  const [relations, setRelations] = useState([]);
  const [relCatalog, setRelCatalog] = useState([]);
  const [relForm, setRelForm] = useState({ to_exercise_id: "", type: "progression" });
  const [relSaving, setRelSaving] = useState(false);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== "all") params.category = category;
      if (scope !== "all") params.scope = scope;
      const res = await exercisesAPI.list(params);
      setItems(res.data.exercises || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Error cargando el catálogo");
    } finally {
      setLoading(false);
    }
  }, [search, category, scope]);

  useEffect(() => {
    const timer = setTimeout(fetchExercises, 300);
    return () => clearTimeout(timer);
  }, [fetchExercises]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (ex) => {
    setEditing(ex.id);
    setForm({
      name: ex.name || "",
      category: ex.category || "other",
      body_parts: (ex.body_parts || []).join(", "),
      equipment: (ex.equipment || []).join(", "),
      tags: (ex.tags || []).join(", "),
      description: ex.description || "",
      instructions: ex.instructions || "",
    });
    setShowDialog(true);
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      body_parts: toList(form.body_parts),
      equipment: toList(form.equipment),
      tags: toList(form.tags),
      description: form.description,
      instructions: form.instructions,
    };
    try {
      if (editing) {
        await exercisesAPI.update(editing, payload);
        toast.success("Ejercicio actualizado");
      } else {
        await exercisesAPI.create(payload);
        toast.success("Ejercicio creado");
      }
      setShowDialog(false);
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ex) => {
    if (!window.confirm(`¿Eliminar "${ex.name}"?`)) return;
    try {
      await exercisesAPI.delete(ex.id);
      toast.success("Ejercicio eliminado");
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  // ── progressions ──
  const nameOf = (id) => relCatalog.find((e) => e.id === id)?.name || "Ejercicio";

  const openRelations = async (ex) => {
    setRelExercise(ex);
    setRelForm({ to_exercise_id: "", type: "progression" });
    try {
      const [relRes, catRes] = await Promise.all([
        exercisesAPI.relations(ex.id),
        relCatalog.length ? Promise.resolve(null) : exercisesAPI.list(),
      ]);
      setRelations(relRes.data.relations || []);
      if (catRes) setRelCatalog(catRes.data.exercises || []);
    } catch {
      toast.error("Error cargando progresiones");
    }
  };

  const addRelation = async () => {
    if (!relForm.to_exercise_id) { toast.error("Selecciona un ejercicio destino"); return; }
    setRelSaving(true);
    try {
      await exercisesAPI.addRelation(relExercise.id, relForm);
      const relRes = await exercisesAPI.relations(relExercise.id);
      setRelations(relRes.data.relations || []);
      setRelForm({ to_exercise_id: "", type: "progression" });
      toast.success("Relación agregada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar");
    } finally {
      setRelSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exercises-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Ejercicios</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{total} ejercicio{total !== 1 ? "s" : ""} en el catálogo</p>
        </div>
        <Button size="sm" className="rounded-full" onClick={openNew} data-testid="new-exercise-btn">
          <Plus className="w-4 h-4 mr-1.5" />
          Nuevo ejercicio
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="exercise-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full sm:w-44" data-testid="exercise-category-filter">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-full sm:w-40" data-testid="exercise-scope-filter">
            <SelectValue placeholder="Origen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="global">Catálogo base</SelectItem>
            <SelectItem value="tenant">Míos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />)}
        </div>
      ) : items.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((ex) => (
            <Card key={ex.id} className="group" data-testid={`exercise-card-${ex.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground leading-tight">{ex.name}</p>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => openRelations(ex)} className="text-muted-foreground hover:text-primary" title="Progresiones" data-testid={`exercise-relations-${ex.id}`}>
                      <GitBranch className="w-3.5 h-3.5" />
                    </button>
                    {ex.scope === "tenant" && (
                      <>
                        <button onClick={() => openEdit(ex)} className="text-muted-foreground hover:text-foreground" title="Editar" data-testid={`exercise-edit-${ex.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(ex)} className="text-muted-foreground hover:text-destructive" title="Eliminar" data-testid={`exercise-delete-${ex.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="secondary" className="text-xs">{catLabel(ex.category)}</Badge>
                  <Badge variant={ex.scope === "global" ? "outline" : "default"} className="text-xs">
                    {ex.scope === "global" ? "Catálogo base" : "Personalizado"}
                  </Badge>
                </div>
                {ex.description ? <p className="text-xs text-muted-foreground line-clamp-2">{ex.description}</p> : null}
                {(ex.body_parts || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {ex.body_parts.slice(0, 4).map((bp) => (
                      <span key={bp} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{bp}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Dumbbell className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">
            {search || category !== "all" || scope !== "all" ? "No se encontraron ejercicios" : "El catálogo está vacío"}
          </p>
          <Button variant="link" size="sm" className="mt-1" onClick={openNew}>Agregar un ejercicio</Button>
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="exercise-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar ejercicio" : "Nuevo ejercicio"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ej: Sentadilla búlgara" required data-testid="exercise-name-input" />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger data-testid="exercise-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Zonas del cuerpo</Label>
                <Input value={form.body_parts} onChange={(e) => setField("body_parts", e.target.value)} placeholder="gluteo, core" data-testid="exercise-bodyparts-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Separadas por coma</p>
              </div>
              <div>
                <Label>Equipo</Label>
                <Input value={form.equipment} onChange={(e) => setField("equipment", e.target.value)} placeholder="banda, mancuerna" data-testid="exercise-equipment-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Separado por coma</p>
              </div>
            </div>
            <div>
              <Label>Etiquetas</Label>
              <Input value={form.tags} onChange={(e) => setField("tags", e.target.value)} placeholder="rehab, tren-inferior" data-testid="exercise-tags-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Separadas por coma (sirven para clínico y performance)</p>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} rows={2} placeholder="Breve descripción..." data-testid="exercise-description-input" />
            </div>
            <div>
              <Label>Instrucciones</Label>
              <Textarea value={form.instructions} onChange={(e) => setField("instructions", e.target.value)} rows={2} placeholder="Indicaciones de ejecución..." data-testid="exercise-instructions-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="exercise-save-btn">
                {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear ejercicio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de progresiones */}
      <Dialog open={!!relExercise} onOpenChange={(o) => !o && setRelExercise(null)}>
        <DialogContent className="sm:max-w-md" data-testid="relations-dialog">
          <DialogHeader>
            <DialogTitle>Progresiones — {relExercise?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {relations.length > 0 ? (
              <div className="space-y-1.5">
                {relations.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-muted/40" data-testid={`relation-row-${r.id}`}>
                    <Badge variant="outline" className="text-[10px]">{relTypeLabel(r.type)}</Badge>
                    <span className="text-foreground">{nameOf(r.to_exercise_id)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Sin relaciones todavía. Agrega una abajo.</p>
            )}
            <div className="border-t border-border pt-3 space-y-2">
              <Label className="text-xs">Agregar relación</Label>
              <Select value={relForm.type} onValueChange={(v) => setRelForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="relation-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={relForm.to_exercise_id} onValueChange={(v) => setRelForm((f) => ({ ...f, to_exercise_id: v }))}>
                <SelectTrigger data-testid="relation-target"><SelectValue placeholder="Ejercicio destino" /></SelectTrigger>
                <SelectContent>
                  {relCatalog.filter((e) => e.id !== relExercise?.id).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="rounded-full w-full" onClick={addRelation} disabled={relSaving} data-testid="relation-add-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />{relSaving ? "Agregando..." : "Agregar relación"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
